import { Component, ChangeDetectionStrategy, inject, computed, signal, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import { I18nService } from '../../i18n/i18n.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'header.component.html',
  styleUrl: 'header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private api = inject(ApiService);
  private router = inject(Router);
  i18n = inject(I18nService);

  incidentActive = signal(false);
  private pollInterval: ReturnType<typeof setInterval> | null = null;

  isAuthenticated = this.auth.isAuthenticated;
  userName = this.auth.userName;
  userGroup = this.auth.userGroup;
  userMenuOpen = signal(false);
  viewsMenuOpen = signal(false);

  initials = computed(() => {
    const name = this.userName();
    if (!name) return '?';
    const parts = name.split(/[@.\s]/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  });

  effectiveSite = computed(() => this.auth.siteOverride() || this.auth.currentSite());

  toggleUserMenu(): void { this.userMenuOpen.set(!this.userMenuOpen()); this.viewsMenuOpen.set(false); }
  toggleViewsMenu(event: Event): void {
    event.stopPropagation();
    this.viewsMenuOpen.set(!this.viewsMenuOpen());
    this.userMenuOpen.set(false);
  }
  isViewsActive(): boolean {
    return this.router.url.startsWith('/logistic');
  }

  ngOnInit(): void {
    if (this.auth.isAuthenticated()) {
      this.refreshSiteStatus();
      this.loadSitesIfSuperAdmin();
    }
    this.pollInterval = setInterval(() => {
      if (this.auth.isAuthenticated()) this.refreshSiteStatus();
      if (this.isSuperAdmin() && this.allSites().length === 0) this.loadSitesIfSuperAdmin();
    }, 15000);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  private loadSitesIfSuperAdmin(): void {
    if (this.auth.hasGroup('super_admin')) {
      this.api.getAllSites().subscribe({ next: (res) => this.allSites.set(res.items) });
    }
  }

  private refreshSiteStatus(): void {
    this.api.getCurrentSite().subscribe({
      next: (s: any) => {
        this.incidentActive.set(s.incident_active ?? false);
      },
      error: () => {},
    });
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.avatar-wrap')) this.userMenuOpen.set(false);
    if (!target.closest('.nav-dropdown')) this.viewsMenuOpen.set(false);
  }

  showRegistration = computed(() => this.auth.hasGroup('guards', 'bcu', 'admin'));
  showQueue = computed(() => this.auth.hasGroup('bcu', 'admin', 'hse', 'guards'));
  showPlanning = computed(() => this.auth.hasGroup('bcu', 'admin'));
  showHistory = computed(() => this.auth.hasGroup('hse', 'bcu', 'admin'));
  showViews = computed(() => this.auth.hasGroup('bcu', 'admin', 'hse', 'guards'));
  showAnalytics = computed(() => this.auth.hasGroup('bcu', 'admin', 'super_admin'));
  showDashboard = computed(() => this.auth.hasGroup('admin', 'super_admin'));
  showSettings = computed(() => this.auth.hasGroup('bcu', 'admin', 'super_admin'));
  isSuperAdmin = computed(() => this.auth.hasGroup('super_admin'));
  allSites = signal<{ code: string; name: string }[]>([]);

  toggleLang(): void { this.i18n.toggle(); }

  switchSite(code: string): void {
    this.auth.switchSite(code);
    window.location.reload();
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }
}
